import { Fragment, useState, useMemo, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { useContentHeightCalculation } from "../../../../../hooks/useContentHeightCalculation";
import PersonalFinancesMenu from "./common/PersonalFinancesMenu";
import DataTable from "../../../../../components/ui/table/DataTable";
import TransactionDetailsModal from "../../../../../components/ui/popup/finances/TransactionDetailsModal";
import LatestTransactionsCarousel from "../../../../../components/ui/carousels/LatestTransactionsCarousel";
import FilterModal from "../../../../../components/ui/popup/FilterModal";
import CustomTransactionDateRangeModal from "../../../../../components/ui/popup/finances/CustomTransactionDateRangeModal";
import LoadingPlaceholder from "../../../../../components/ui/loading/LoadingPlaceholder";
import { useTransactions } from "../../../../../hooks/finances/useTransactions";
import { getUniqueOptions } from "../../../../../components/ui/popup/finances/utils/helpers";
import {
  DEFAULT_TRANSACTION_DATE_FILTER,
  getDateFilterLabel,
  getTransactionRequestParams,
  isDateFilterActive,
  LATEST_TRANSACTION_PARAMS,
  TRANSACTION_DATE_FILTERS,
} from "../../../../../components/ui/popup/finances/utils/transactionHelpers";
import GhostCardContainer from "../../../../../components/ui/loading/skeletons/GhostCardContainer";
import GhostTransactionCard from "../../../../../components/ui/loading/skeletons/GhostTransactionCard";
import GhostTransactionsCarousel from "../../../../../components/ui/loading/skeletons/GhostTransactionCarousel";
import GhostDataTableHeader from "../../../../../components/ui/loading/skeletons/GhostDataTableHeader";
import GhostDataTableBody from "../../../../../components/ui/loading/skeletons/GhostDataTableBody";
import GhostDataTable from "../../../../../components/ui/loading/skeletons/GhostDataTable";
import { useRefreshOnAuth } from "../../../../../hooks/useRefreshOnAuth";
import LoadingToRealTransitionWrapper from "../../../../../components/ui/transitions/LoadingToRealTransitionWraper";
import InstitutionDataLoaderBanner from "./common/InsitutionDataLoaderBanner";
import InstitutionPartialDataWarningBanner from "./common/InstitutionPartialDataWarningBanner";
import { getLanguageContent } from "../../../../../utils/language/languageSelector";
import { PERSONAL_FINANCES_SCHEMA } from "../../../../../utils/schemas/language/pages/personalFinancesSchema";
import { FILTER_SCHEMA } from "../../../../../utils/schemas/language/components/filterSchema";

function PersonalTransactions() {
  const content = getLanguageContent(PERSONAL_FINANCES_SCHEMA);
  const filterContent = getLanguageContent(FILTER_SCHEMA);
  const { contentRef, mainRef } = useContentHeightCalculation();

  // ============================================================
  // PIPELINE SYNC STATE EXTRACTION & CORRECTED EVALUATIONS
  // ============================================================
  const { institutionSyncStates = [], loadSyncStates } =
    useOutletContext() || {};

  // Rule 1: All connected institutions have transaction_first_load_completed to false
  const isEmptyOnboarding = useMemo(() => {
    if (institutionSyncStates.length === 0) return false;
    return institutionSyncStates.every(
      (inst) => inst.transaction_first_load_completed === false,
    );
  }, [institutionSyncStates]);

  // Rule 2: At least 1 institution has transaction_history_completed to false
  const isPartialSyncing = useMemo(() => {
    if (isEmptyOnboarding || institutionSyncStates.length === 0) return false;
    return institutionSyncStates.some(
      (inst) => inst.transaction_history_completed === false,
    );
  }, [institutionSyncStates, isEmptyOnboarding]);

  // Extract names of institutions that are currently compiling history to display in the banner
  const processingInstitutionsText = useMemo(() => {
    const pendingNames = institutionSyncStates
      .filter((inst) => inst.transaction_history_completed === false)
      .map((inst) => inst.institution_name || "Unknown Institution");

    if (pendingNames.length === 0) return "";
    if (pendingNames.length === 1) return pendingNames[0];
    if (pendingNames.length === 2)
      return `${pendingNames[0]} & ${pendingNames[1]}`;
    return `${pendingNames.slice(0, -1).join(", ")}, & ${
      pendingNames[pendingNames.length - 1]
    }`;
  }, [institutionSyncStates]);

  const [appliedDateFilter, setAppliedDateFilter] = useState(
    DEFAULT_TRANSACTION_DATE_FILTER,
  );
  const [appliedCustomDateRange, setAppliedCustomDateRange] = useState(null);
  const [isCustomDateModalOpen, setIsCustomDateModalOpen] = useState(false);
  const transactionRequestParams = useMemo(
    () =>
      getTransactionRequestParams(appliedDateFilter, appliedCustomDateRange),
    [appliedDateFilter, appliedCustomDateRange],
  );

  const {
    transactions,
    isLoading: isLoadingTransactions,
    error: transactionsError,
    loadTransactions,
    updateTransactionInState,
  } = useTransactions(transactionRequestParams);

  const {
    latestTransactions,
    isLoading: isLoadingLatestTransactions,
    loadTransactions: loadLatestTransactions,
    updateTransactionInState: updateLatestTransactionInState,
  } = useTransactions(LATEST_TRANSACTION_PARAMS);

  const refreshTransactionsOnAuth = useCallback(() => {
    loadTransactions();
    loadLatestTransactions();
  }, [loadLatestTransactions, loadTransactions]);

  useRefreshOnAuth(refreshTransactionsOnAuth);

  const [merchantSearch, setMerchantSearch] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState({
    institution_uuid: "",
    account_uuid: "",
    category: "",
  });
  const [activeFilters, setActiveFilters] = useState({
    institution_uuid: "",
    account_uuid: "",
    category: "",
  });

  const headers = [
    {
      label: content.transactions.headers.merchant,
      key: "merchant",
      sortable: false,
      width: "200px",
    },
    {
      label: content.transactions.headers.category,
      key: "category",
      sortable: false,
      type: "category",
      width: "200px",
    },
    {
      label: content.transactions.headers.amount,
      key: "amount",
      sortable: true,
      width: "200px",
    },
    {
      label: content.transactions.headers.date,
      key: "date",
      sortable: true,
      type: "tag",
      width: "200px",
    },
  ];

  const getRowsForFilterOptions = useCallback(
    (excludedField) => {
      return transactions.filter((transaction) => {
        return (
          (excludedField === "institution_uuid" ||
            !draftFilters.institution_uuid ||
            transaction.institution_uuid === draftFilters.institution_uuid) &&
          (excludedField === "account_uuid" ||
            !draftFilters.account_uuid ||
            transaction.account_uuid === draftFilters.account_uuid) &&
          (excludedField === "category" ||
            !draftFilters.category ||
            transaction.category_primary === draftFilters.category)
        );
      });
    },
    [draftFilters, transactions],
  );

  const institutionOptions = useMemo(
    () =>
      getUniqueOptions(
        getRowsForFilterOptions("institution_uuid"),
        "institution_uuid",
        "institution_name",
      ),
    [getRowsForFilterOptions],
  );

  const accountOptions = useMemo(
    () =>
      getUniqueOptions(
        getRowsForFilterOptions("account_uuid"),
        "account_uuid",
        "account_name",
      ),
    [getRowsForFilterOptions],
  );

  const categoryOptions = useMemo(
    () =>
      getUniqueOptions(
        getRowsForFilterOptions("category"),
        "category_primary",
        "category",
      ),
    [getRowsForFilterOptions],
  );

  const filteredTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      return (
        (!activeFilters.institution_uuid ||
          transaction.institution_uuid === activeFilters.institution_uuid) &&
        (!activeFilters.account_uuid ||
          transaction.account_uuid === activeFilters.account_uuid) &&
        (!activeFilters.category ||
          transaction.category_primary === activeFilters.category)
      );
    });
  }, [activeFilters, transactions]);

  const handleFilterChange = (field, value) => {
    setDraftFilters((currentFilters) => {
      const nextFilters = {
        ...currentFilters,
        [field]: value,
        ...(field === "institution_uuid" ? { account_uuid: "" } : {}),
      };

      const hasMatchingCategory = transactions.some((transaction) => {
        return (
          (!nextFilters.institution_uuid ||
            transaction.institution_uuid === nextFilters.institution_uuid) &&
          (!nextFilters.account_uuid ||
            transaction.account_uuid === nextFilters.account_uuid) &&
          (!nextFilters.category ||
            transaction.category_primary === nextFilters.category)
        );
      });

      return hasMatchingCategory
        ? nextFilters
        : {
            ...nextFilters,
            category: "",
          };
    });
  };

  const handleOpenFilters = () => {
    setDraftFilters(activeFilters);
    setIsFilterOpen(true);
  };

  const handleCloseFilters = () => {
    setDraftFilters(activeFilters);
    setIsFilterOpen(false);
  };

  const handleApplyFilters = () => {
    setIsFilterOpen(false);
    setActiveFilters(draftFilters);
  };

  const handleDateFilterClick = (filterValue) => {
    if (filterValue === "custom") {
      setIsCustomDateModalOpen(true);
      return;
    }

    setAppliedDateFilter(filterValue);
    setAppliedCustomDateRange(null);
    setIsCustomDateModalOpen(false);
  };

  const handleApplyCustomDateRange = ({ start_date, end_date }) => {
    setAppliedCustomDateRange({ start_date, end_date });
    setAppliedDateFilter("custom");
    setIsCustomDateModalOpen(false);
  };

  const handleCloseCustomDateModal = () => {
    setIsCustomDateModalOpen(false);
  };

  const handleSelectedTransactionUpdate = (updatedTransaction) => {
    setSelectedTransaction(updatedTransaction);
    updateTransactionInState(updatedTransaction);
    updateLatestTransactionInState(updatedTransaction);
  };

  const handleLatestTransactionUpdate = useCallback(
    (updatedTransaction) => {
      updateLatestTransactionInState(updatedTransaction);
      updateTransactionInState(updatedTransaction);
    },
    [updateLatestTransactionInState, updateTransactionInState],
  );

  const getTransactionActions = (row) => [
    {
      label: content.transactions.actions.view_details,
      onClick: () => setSelectedTransaction(row),
    },
  ];

  const searchRightElement = (
    <div
      className="search-input-element"
      style={{ position: "relative", display: "flex", alignItems: "center" }}
    >
      <div className="form-field" style={{ flex: 1 }}>
        <input
          className="form-input"
          type="text"
          placeholder={content.transactions.search_placeholder}
          value={merchantSearch}
          onChange={(event) => setMerchantSearch(event.target.value)}
          style={{ paddingRight: "40px" }}
        />
      </div>
      <img
        src={`${process.env.PUBLIC_URL}/images/icons/search_blue.svg`}
        alt=""
        style={{
          position: "absolute",
          right: "10px",
          width: "22px",
          pointerEvents: "none",
        }}
      />
    </div>
  );

  const dateFilterElement = (
    <div className="utc-header-filtering transactions-date-filter">
      {TRANSACTION_DATE_FILTERS.map((filter) => {
        const isCustomWithAppliedRange =
          filter.value === "custom" &&
          appliedDateFilter === "custom" &&
          appliedCustomDateRange?.start_date &&
          appliedCustomDateRange?.end_date;

        return (
          <div
            key={filter.value}
            className={`utc-header-filtering-item ${
              isDateFilterActive(
                filter.value,
                appliedDateFilter,
                isCustomDateModalOpen,
              )
                ? "active"
                : ""
            } ${
              isCustomWithAppliedRange
                ? "utc-header-filtering-item-custom-label"
                : ""
            }`}
            onClick={() => handleDateFilterClick(filter.value)}
            style={{ cursor: "pointer" }}
          >
            {getDateFilterLabel(
              filter,
              appliedDateFilter,
              appliedCustomDateRange,
              content.transactions.date_filters,
            )}
          </div>
        );
      })}
    </div>
  );

  // ============================================================
  // INTERCEPTOR 1: FULL EMPTY STATE ONBOARDING (NO DATA READY AT ALL)
  // ============================================================
  if (isEmptyOnboarding) {
    return (
      <InstitutionDataLoaderBanner
        content={content.status.transactions.no_data}
        refreshContent={content.global.refresh_in}
        onRefresh={loadSyncStates}
      />
    );
  }

  return (
    <Fragment>
      {/* ============================================================
          INTERCEPTOR 2: PARTIAL SYNC STATUS BANNER (SPECIFYING TARGETS)
          ============================================================ */}
      {isPartialSyncing && (
        <InstitutionPartialDataWarningBanner
          onRefresh={loadSyncStates}
          content={content.status.transactions.processing}
          processingText={processingInstitutionsText}
          refreshText={content.global.refresh}
        />
      )}

      <>
        {transactionsError && (
          <p className="text-danger" style={{ marginBottom: "12px" }}>
            {transactionsError}
          </p>
        )}

        <LoadingToRealTransitionWrapper
          isLoading={isLoadingLatestTransactions}
          ghost={
            <GhostCardContainer>
              <GhostTransactionsCarousel count={3} />
            </GhostCardContainer>
          }
        >
          <LatestTransactionsCarousel
            rows={latestTransactions}
            onTransactionUpdate={handleLatestTransactionUpdate}
          />
        </LoadingToRealTransitionWrapper>

        <div className="mobile-container-separator"></div>

        <LoadingToRealTransitionWrapper
          isLoading={isLoadingTransactions}
          ghost={
            <GhostCardContainer>
              <GhostDataTable
                headers={headers}
                rows={2}
                title={true}
                description={true}
                showSearch={true}
              />
            </GhostCardContainer>
          }
        >
          <DataTable
            title={content.transactions.table_title}
            headerMiddleElement={dateFilterElement}
            headers={headers}
            rows={filteredTransactions}
            getActionMenu={getTransactionActions}
            rightElement={searchRightElement}
            filterElement={true}
            onFilterClick={handleOpenFilters}
            searchQuery={merchantSearch}
            searchHeaders={["merchant"]}
            emptyStateTitle={content.transactions.empty}
            emptyStateText={content.transactions.empty}
          />
        </LoadingToRealTransitionWrapper>
      </>

      {selectedTransaction && (
        <TransactionDetailsModal
          transaction={selectedTransaction}
          onTransactionUpdate={handleSelectedTransactionUpdate}
          onClose={() => setSelectedTransaction(null)}
          copy={content.transactions.details}
          commonCopy={content.global}
        />
      )}

      <CustomTransactionDateRangeModal
        isOpen={isCustomDateModalOpen}
        onClose={handleCloseCustomDateModal}
        onApply={handleApplyCustomDateRange}
        initialFromDate={appliedCustomDateRange?.start_date || ""}
        initialToDate={appliedCustomDateRange?.end_date || ""}
        copy={content.transactions.date_filters.modal}
      />

      <FilterModal
        isOpen={isFilterOpen}
        onClose={handleCloseFilters}
        onApply={handleApplyFilters}
        title={content.transactions.filter_title}
        applyText={filterContent.apply}
      >
        <div className="filter-field">
          <select
            className="form-input"
            value={draftFilters.institution_uuid}
            onChange={(e) =>
              handleFilterChange("institution_uuid", e.target.value)
            }
          >
            <option value="">{filterContent.all_institutions}</option>
            {institutionOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-field">
          <select
            className="form-input"
            value={draftFilters.account_uuid}
            onChange={(e) => handleFilterChange("account_uuid", e.target.value)}
          >
            <option value="">{filterContent.all_accounts}</option>
            {accountOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-field">
          <select
            className="form-input"
            value={draftFilters.category}
            onChange={(e) => handleFilterChange("category", e.target.value)}
          >
            <option value="">{filterContent.all_categories}</option>
            {categoryOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </FilterModal>
    </Fragment>
  );
}

export default PersonalTransactions;
